import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import FaqAssistMark, { FaqAssistAiIcon } from "./FaqAssistMark";
import "./FaqAssistUnavailableModal.scss";

/**
 * Shown when AQ Assist is clicked but the organisation does not have access.
 * Step 1: message + Create support ticket.
 * Step 2 (after click): request note + submit ticket.
 */
export default function FaqAssistUnavailableModal({
  open,
  message,
  submitting = false,
  onClose,
  onSubmitRequest,
}) {
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestNote, setRequestNote] = useState("");
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    if (!open) {
      setShowRequestForm(false);
      setRequestNote("");
      setLocalError("");
    }
  }, [open]);

  if (!open) return null;

  const body =
    String(message || "").trim() ||
    "AQ Assist is not enabled for your organisation yet.";

  const handleSubmit = () => {
    const note = requestNote.trim();
    if (!note) {
      setLocalError("Please enter a short request for the support team.");
      return;
    }
    setLocalError("");
    onSubmitRequest?.(note);
  };

  return createPortal(
    <div
      className="faq-assist-unavail"
      role="presentation"
      onClick={submitting ? undefined : onClose}
    >
      <div
        className="faq-assist-unavail__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="faq-assist-unavail-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="faq-assist-unavail__close"
          onClick={onClose}
          disabled={submitting}
          aria-label="Close"
        >
          <i className="fas fa-times" aria-hidden="true" />
        </button>

        <div className="faq-assist-unavail__hero" aria-hidden="true">
          <span className="faq-assist-unavail__icon">
            <FaqAssistMark bordered />
          </span>
        </div>

        <h2 id="faq-assist-unavail-title" className="faq-assist-unavail__title">
          <span>
            AQ Assist
            <FaqAssistAiIcon />
          </span>
          <span>is not available</span>
        </h2>
        <p className="faq-assist-unavail__lead">{body}</p>

        {showRequestForm ? (
          <>
            <label
              className="faq-assist-unavail__label"
              htmlFor="faq-assist-unavail-note"
            >
              Your request
            </label>
            <textarea
              id="faq-assist-unavail-note"
              className="faq-assist-unavail__input"
              rows={3}
              maxLength={1000}
              placeholder="Tell the AQ team why you need AQ Assist enabled…"
              value={requestNote}
              disabled={submitting}
              autoFocus
              onChange={(e) => {
                setRequestNote(e.target.value);
                if (localError) setLocalError("");
              }}
            />
            {localError ? (
              <p className="faq-assist-unavail__error">{localError}</p>
            ) : (
              <p className="faq-assist-unavail__hint">
                Submit to create a support ticket, or email{" "}
                <a href="mailto:support@aq-trials.com">support@aq-trials.com</a>.
              </p>
            )}
          </>
        ) : null}

        <div className="faq-assist-unavail__actions">
          <button
            type="button"
            className="faq-assist-unavail__btn faq-assist-unavail__btn--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            Close
          </button>
          {!showRequestForm ? (
            <button
              type="button"
              className="faq-assist-unavail__btn faq-assist-unavail__btn--primary"
              onClick={() => setShowRequestForm(true)}
            >
              <i className="fas fa-headset" aria-hidden="true" />
              Create support ticket
            </button>
          ) : (
            <button
              type="button"
              className="faq-assist-unavail__btn faq-assist-unavail__btn--primary"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                  Creating…
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane" aria-hidden="true" />
                  Submit request
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
